import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  Check,
  GitMerge,
  GraduationCap,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import type { LearningTerm, LearningTermKind } from '@/types/learning.types'
import { PageTitle } from '@/components/PageTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/animate-ui/components/radix/alert-dialog'
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateLearningTerm,
  useDeleteLearningTerm,
  useLearningTerms,
  useMergeLearningTerm,
  useRenameLearningTerm,
  useSetLearningTermOnboarding,
} from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'

export const Route = createFileRoute('/_app/learning/admin/properties')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.author),
  component: RouteComponent,
})

const SECTIONS: ReadonlyArray<{
  kind: LearningTermKind
  title: string
  blurb: string
  placeholder: string
}> = [
  {
    kind: 'CATEGORY',
    title: 'Categories',
    blurb: 'What a course is about. One per course.',
    placeholder: 'Compliance',
  },
  {
    kind: 'TECHNOLOGY',
    title: 'Technologies',
    blurb: 'Which tool a course covers, when it covers one.',
    placeholder: 'RightCapital',
  },
]

/**
 * Managing the properties courses are filed under (sc-1245).
 *
 * Its own page rather than a dialog: this is where the vocabulary of the whole
 * catalogue is decided, and it needs room to show what each name costs — how
 * many courses would be affected by a rename, and what a delete would unfile.
 *
 * The two sections are identical in behaviour because a category and a
 * technology are the same kind of thing, so they share a component rather than
 * being written twice.
 */
function RouteComponent() {
  const [kind, setKind] = React.useState<LearningTermKind>('CATEGORY')

  return (
    <div className="py-4">
      <PageTitle title="Learning — Properties" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Learning
        </Link>
      </Button>

      <div className="mb-6">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          Properties
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The categories and technologies courses are filed under, and that
          people browse by. Renaming one moves every course carrying it.
        </p>
      </div>

      {/* One type at a time, full width. Side by side, each row had to fit a
          name, a count, an onboarding switch, rename, merge and delete into
          half a screen — which is why it read as cramped. Nothing here needs
          comparing across the two types, so nothing is lost by separating
          them. */}
      <Tabs
        value={kind}
        onValueChange={(next) => setKind(next as LearningTermKind)}
      >
        <TabsList className="mb-4">
          {SECTIONS.map((section) => (
            <TabsTrigger key={section.kind} value={section.kind}>
              {section.title}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {SECTIONS.filter((section) => section.kind === kind).map((section) => (
        <TermSection key={section.kind} {...section} />
      ))}
    </div>
  )
}

function TermSection({
  kind,
  title,
  blurb,
  placeholder,
}: {
  kind: LearningTermKind
  title: string
  blurb: string
  placeholder: string
}) {
  const { data: terms, isPending } = useLearningTerms(kind)
  const create = useCreateLearningTerm()
  const rename = useRenameLearningTerm()
  const merge = useMergeLearningTerm()
  const remove = useDeleteLearningTerm()
  const setOnboarding = useSetLearningTermOnboarding()

  const [newName, setNewName] = React.useState('')
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState('')
  const [mergingId, setMergingId] = React.useState<string | null>(null)
  const [mergeTarget, setMergeTarget] = React.useState('')
  const [confirmDelete, setConfirmDelete] = React.useState<LearningTerm | null>(
    null,
  )

  const [search, setSearch] = React.useState('')

  const all = terms ?? []
  // A search box only earns its place once scanning stops being enough. Below
  // that it is one more thing between an admin and the list they came for.
  const showSearch = all.length > 8
  const needle = search.trim().toLowerCase()
  const rows =
    showSearch && needle
      ? all.filter((term) => term.name.toLowerCase().includes(needle))
      : all

  const onboardingCount = all.filter((term) => term.countsAsOnboarding).length
  const unusedCount = all.filter((term) => term.courseCount === 0).length

  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{blurb}</p>
          {/* Say where the flag bites, next to the switch that sets it —
              "counts as onboarding" means nothing on its own. */}
          {kind === 'CATEGORY' && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <GraduationCap
                className="mt-0.5 size-3.5 shrink-0 text-primary"
                aria-hidden
              />
              <span>
                Switch on <span className="font-medium">Onboarding</span> and
                every course in that category becomes an onboarding course: new
                starters stay in the Onboarding panel on{' '}
                <Link
                  to="/learning/admin"
                  className="underline underline-offset-2"
                >
                  Learning
                </Link>{' '}
                until they finish it, rather than dropping off after 60 days
                with training outstanding.
              </span>
            </p>
          )}
          {/* What the list is actually made of, so an admin can see the two
              things worth acting on — what counts as onboarding, and what
              nothing uses — without reading every row. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {all.length} {all.length === 1 ? title.slice(0, -3) : title}
            </span>
            {kind === 'CATEGORY' && onboardingCount > 0 && (
              <span className="inline-flex items-center gap-1 text-primary">
                <GraduationCap className="size-3" aria-hidden />
                {onboardingCount} count as onboarding
              </span>
            )}
            {unusedCount > 0 && <span>{unusedCount} on no course</span>}
          </div>
        </div>

        {showSearch && (
          <div className="relative">
            <Search
              className="absolute top-2.5 left-2.5 size-4 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${title.toLowerCase()}`}
              aria-label={`Search ${title.toLowerCase()}`}
              className="pl-8"
            />
          </div>
        )}

        {/* ── Create ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder={placeholder}
            aria-label={`New ${title.toLowerCase()}`}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !newName.trim()) return
              create.mutate(
                { kind, name: newName.trim() },
                { onSuccess: () => setNewName('') },
              )
            }}
          />
          <Button
            size="sm"
            disabled={newName.trim().length === 0 || create.isPending}
            onClick={() =>
              create.mutate(
                { kind, name: newName.trim() },
                { onSuccess: () => setNewName('') },
              )
            }
          >
            <Plus className="mr-1 size-4" aria-hidden />
            Add
          </Button>
        </div>

        {/* ── List ───────────────────────────────────────────────────── */}
        {isPending ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {needle ? `Nothing matches “${search.trim()}”.` : 'None yet.'}
          </p>
        ) : (
          <ul className="divide-y">
            {rows.map((term) => (
              <li key={term.id} className="py-2.5">
                {editingId === term.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      autoFocus
                      aria-label={`Rename ${term.name}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Save"
                      disabled={
                        draft.trim().length === 0 ||
                        draft.trim() === term.name ||
                        rename.isPending
                      }
                      onClick={() =>
                        rename.mutate(
                          { termId: term.id, name: draft.trim() },
                          { onSuccess: () => setEditingId(null) },
                        )
                      }
                    >
                      <Check className="size-4" aria-hidden />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Cancel"
                      onClick={() => setEditingId(null)}
                    >
                      <X className="size-4" aria-hidden />
                    </Button>
                  </div>
                ) : mergingId === term.id ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Move {term.courseCount}{' '}
                      {term.courseCount === 1 ? 'course' : 'courses'} from “
                      {term.name}” into:
                    </p>
                    <div className="flex items-center gap-2">
                      <Select
                        value={mergeTarget}
                        onValueChange={setMergeTarget}
                      >
                        <SelectTrigger aria-label="Merge into">
                          <SelectValue placeholder="Pick one" />
                        </SelectTrigger>
                        <SelectContent>
                          {rows
                            .filter((option) => option.id !== term.id)
                            .map((option) => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        disabled={!mergeTarget || merge.isPending}
                        onClick={() =>
                          merge.mutate(
                            { termId: term.id, targetId: mergeTarget },
                            {
                              onSuccess: () => {
                                setMergingId(null)
                                setMergeTarget('')
                              },
                            },
                          )
                        }
                      >
                        Merge
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Cancel"
                        onClick={() => setMergingId(null)}
                      >
                        <X className="size-4" aria-hidden />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {term.name}
                      </p>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      {term.courseCount}
                    </Badge>
                    {/* Categories only: a technology says which tool a course
                        covers, which has no bearing on onboarding. */}
                    {kind === 'CATEGORY' && (
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Switch
                          id={`onboarding-${term.id}`}
                          checked={term.countsAsOnboarding}
                          disabled={setOnboarding.isPending}
                          onCheckedChange={(next) =>
                            setOnboarding.mutate({
                              termId: term.id,
                              countsAsOnboarding: next,
                            })
                          }
                        />
                        <Label
                          htmlFor={`onboarding-${term.id}`}
                          className="cursor-pointer text-[11px] font-normal text-muted-foreground"
                        >
                          Onboarding
                        </Label>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(term.id)
                        setDraft(term.name)
                      }}
                    >
                      Rename
                    </Button>
                    {/* Merge only exists once there is something to merge INTO,
                        which is also the only time a duplicate can exist. */}
                    {rows.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Merge ${term.name}`}
                        onClick={() => {
                          setMergingId(term.id)
                          setMergeTarget('')
                        }}
                      >
                        <GitMerge className="size-4" aria-hidden />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Delete ${term.name}`}
                      onClick={() => setConfirmDelete(term)}
                    >
                      <Trash2 className="size-4 text-destructive" aria-hidden />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{confirmDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && confirmDelete.courseCount > 0
                ? `${confirmDelete.courseCount} ${
                    confirmDelete.courseCount === 1 ? 'course' : 'courses'
                  } will lose this label. The courses themselves are untouched — only the filing goes.`
                : 'Nothing uses it, so nothing else changes.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDelete) remove.mutate(confirmDelete.id)
                setConfirmDelete(null)
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
